import { compileSchema } from '@toolproof-core/validation/_pure/schema-compilation';

import momentSchema from '@/_contract/_@schema/directionMoment.schema.json' with { type: 'json' };
import ruleSchema from '@/_contract/_@schema/directionRule.schema.json' with { type: 'json' };
import { checkMomentSelector, matchesMomentSelector } from '@/_pure/directionMoment';
import type { MomentInstant, MomentVocabulary } from '@/_contract/_~generated/directionMomentSchema';
import type { MomentIssue } from '@/_contract/directionMoment';
import type { DirectionCatalog, DirectionOverrides, DirectionRule } from '@/_contract/directionRule';

export function resolveDirectionOverrides<TEffect>(
    catalog: DirectionCatalog<TEffect>, overrides?: DirectionOverrides<TEffect>,
): DirectionCatalog<TEffect> {
    if (!overrides) return catalog;
    const keys = new Set(catalog.rules.map(rule => rule.key));
    const replacements = new Map<string, NonNullable<DirectionOverrides<TEffect>['replace']>[number]>();
    for (const replacement of overrides.replace ?? []) {
        if (!keys.has(replacement.key)) throw new Error(`Unknown direction replacement key '${replacement.key}'.`);
        if (replacements.has(replacement.key)) throw new Error(`Duplicate direction replacement key '${replacement.key}'.`);
        replacements.set(replacement.key, replacement);
    }
    const combined = composeDirectionCatalogs(catalog, { rules: overrides.rules ?? [] });
    const allKeys = new Set(combined.rules.map(rule => rule.key));
    const included = new Set(overrides.include ?? []);
    const excluded = new Set(overrides.exclude ?? []);
    for (const key of [...included, ...excluded]) {
        if (!allKeys.has(key)) throw new Error(`Unknown direction inclusion key '${key}'.`);
        if (included.has(key) && excluded.has(key)) throw new Error(`Direction rule '${key}' is both included and excluded.`);
    }
    return { rules: combined.rules.filter(rule => !excluded.has(rule.key)).map(rule => {
        const replacement = replacements.get(rule.key);
        return { ...rule, ...replacement, ...(included.has(rule.key) ? { inclusion: 'always' as const } : {}) };
    }) };
}

export function parseDirectionOverrides<TEffect>(
    value: unknown, catalog: DirectionCatalog<TEffect>,
    parseCatalog: (value: unknown) => DirectionCatalog<TEffect>,
): DirectionOverrides<TEffect> | undefined {
    if (value === undefined) return undefined;
    const validator = compileSchema(ruleSchema, { references: [momentSchema] }).fragment('#/$defs/DirectionOverrides');
    if (!validator) throw new Error('Missing DirectionOverrides schema.');
    const result = validator.validate(value);
    if (!result.valid) {
        throw new Error(`Invalid direction overrides: ${result.issues.map(issue => `${issue.instancePath} ${issue.message}`).join('; ')}`);
    }
    const overrides = value as DirectionOverrides<TEffect>;
    // Validate even excluded replacements and local rules, not only the surviving selection.
    parseCatalog(resolveDirectionOverrides(catalog, { ...overrides, include: [], exclude: [] }));
    parseCatalog(resolveDirectionOverrides(catalog, overrides));
    return overrides;
}

export function parseDirectionCatalog<TEffect>(
    value: unknown, vocabulary: MomentVocabulary, parseEffect: (effect: unknown) => TEffect,
): DirectionCatalog<TEffect> {
    const validator = compileSchema(ruleSchema, { references: [momentSchema] }).fragment('#/$defs/DirectionCatalog');
    if (!validator) throw new Error('Missing DirectionCatalog schema.');
    const result = validator.validate(value);
    if (!result.valid) {
        throw new Error(`Invalid direction catalog: ${result.issues.map(issue => `${issue.instancePath} ${issue.message}`).join('; ')}`);
    }
    const catalog = value as DirectionCatalog;
    const issues = checkDirectionCatalog(catalog, vocabulary);
    if (issues.length) {
        throw new Error(`Invalid direction catalog: ${issues.map(issue => `${issue.path} ${issue.message}`).join('; ')}`);
    }
    return { rules: catalog.rules.map(rule => ({ ...rule, effect: parseEffect(rule.effect) })) };
}

/** Unites direction catalogs, such as a host's extension of an embedded adapter's catalog; a key declared twice throws. */
export function composeDirectionCatalogs<TEffect>(...catalogs: readonly DirectionCatalog<TEffect>[]): DirectionCatalog<TEffect> {
    const seen = new Set<string>();
    const rules: DirectionRule<TEffect>[] = [];
    for (const catalog of catalogs) {
        for (const rule of catalog.rules) {
            if (seen.has(rule.key)) throw new Error(`Direction catalogs both declare the rule key '${rule.key}'.`);
            seen.add(rule.key);
            rules.push(rule);
        }
    }
    return { rules };
}

/** Checks a schema-valid catalog against an adapter vocabulary: duplicate keys and selectors referring to undeclared kinds. */
export function checkDirectionCatalog<TEffect>(catalog: DirectionCatalog<TEffect>, vocabulary: MomentVocabulary): MomentIssue[] {
    const issues: MomentIssue[] = [];
    const seen = new Set<string>();
    catalog.rules.forEach((rule, index) => {
        const path = `/rules/${index}`;
        if (seen.has(rule.key)) issues.push({ path: `${path}/key`, message: `Duplicate rule key '${rule.key}'.` });
        seen.add(rule.key);
        for (const issue of checkMomentSelector(rule.selector, vocabulary)) {
            issues.push({ path: `${path}/selector${issue.path}`, message: issue.message });
        }
    });
    return issues;
}

/**
 * Selects the rules of `catalog` that match `instant` and are included: 'always' rules unconditionally,
 * 'opt-in' rules only when `isIncluded` accepts their key. Order is preserved from the catalog, so composing
 * catalogs with the more specific one last lets a caller's merge give it precedence over the more general one.
 */
export function selectDirectionRules<TEffect>(
    catalog: DirectionCatalog<TEffect>,
    instant: MomentInstant,
    isIncluded: (key: string) => boolean = () => false,
): DirectionRule<TEffect>[] {
    return catalog.rules.filter(rule =>
        matchesMomentSelector(rule.selector, instant) && (rule.inclusion === 'always' || isIncluded(rule.key)));
}

/**
 * Applies a catalog to an instant and returns the selected effects in the same catalog order.
 * This is the usual adapter-level derivation path: the adapter defines `effect`, and the caller decides
 * how to merge or render the returned list.
 */
export function selectDirectionRuleEffects<TEffect>(
    catalog: DirectionCatalog<TEffect>,
    instant: MomentInstant,
    isIncluded: (key: string) => boolean = () => false,
): readonly TEffect[] {
    return selectDirectionRules(catalog, instant, isIncluded).map(rule => rule.effect);
}
